import { AiOutlineArrowRight } from "react-icons/ai";
import { useNavigate, useSearchParams } from "react-router-dom";

import Button from "@Components/Button";
import ApplicantFilter from "@Components/Filters/ApplicantFilter";
import DateFilter from "@Components/Filters/DateFilter";
import SelectFilter from "@Components/Filters/SelectFilter";
import TextFilter from "@Components/Filters/TextFilter";
import {
  DateFilterPreviewProps,
  FilterPreviewProps,
} from "@Components/Filters/types";
import IconButton from "@Components/IconButton";
import Table from "@Components/Table";
import { Column } from "@Components/Table/types";
import useGetDeals from "@Hooks/useDeals";
import { Deal } from "@Types/deal";
import { DmvStatus, getDmvStatus, TitleIssue } from "@Types/title_issues";
import {
  addDaysToDate,
  capitalize,
  formatDate,
  formatFirstAndLastNames,
} from "@Utils/functions";

import useCurrentUser from "@Hooks/useCurrentUser";
import { hasActionPermissions } from "@Utils/permissions";
import States from "@Utils/states";
import styles from "./Applications.module.scss";
import { TitleStatusChip } from "@Pages/TitleIssues/TitleIssueStatusChip/TitleIssueStatusChip";

export const getNextCheckDate = (titleIssue: TitleIssue) => {
  if (!titleIssue?.data?.info.lastCheckedDate) {
    return formatDate(new Date());
  }
  return formatDate(
    addDaysToDate(new Date(titleIssue?.data?.info.lastCheckedDate), 15)
  );
};

export const formatCustomersNames = (deal: Deal) => {
  return (
    `${formatFirstAndLastNames(
      deal?.data?.applicant?.data?.info?.firstName,
      deal?.data?.applicant?.data?.info?.lastName
    )}
    ${
      deal?.data?.coApplicant?.data?.info?.firstName
        ? " / " +
          formatFirstAndLastNames(
            deal?.data?.coApplicant?.data?.info?.firstName,
            deal?.data?.coApplicant?.data?.info?.lastName
          )
        : ""
    }`?.trim() || "N/A"
  );
};

export const statusToColor = (status?: DmvStatus) =>
  status === "untitled"
    ? "#ffbb6c"
    : status === "not_checked"
    ? "#6B97F6"
    : status === "titled"
    ? "#10b981"
    : "#ffbb6c";

export const statusStyle = (background: string): React.CSSProperties => ({
  fontWeight: 700,
  fontSize: 18,
  background,

  borderRadius: "5px",
  justifyContent: "center",
  width: "90%",
  color: "#FFF",
  maxWidth: "250px",
  whiteSpace: "pre-wrap",
  textAlign: "center",
});

export const formatCollateral = (deal: Deal) =>
  [deal?.data?.info?.vehicle?.make, deal?.data?.info?.vehicle?.model]
    ?.filter((x) => x)
    ?.join(" - ") || "N/A";
const Applications = ({ dealerId }: { dealerId?: string }) => {
  const navigate = useNavigate();
  const user = useCurrentUser();
  const [searchParams] = useSearchParams();
  const currentPage = Number(searchParams.get("loanPage")) || 1;
  const columns: Column<Deal>[] = [
    ...(hasActionPermissions("lender_ticket", "read", user)
      ? ([
          {
            id: "status",
            label: "Title Status",
            truncate: 15,

            value: (deal) => (
              <TitleStatusChip
                status={getDmvStatus(
                  deal?.data?.titleIssue?.data?.info.dmvInfo.status
                )}
              />
            ),
            sortPath: [
              "data",
              "titleIssue",
              "data",
              "info",
              "dmvInfo",
              "status",
            ],
            filters: [
              {
                placeholder: "Status",
                preview: SelectFilter,
                queryPath: [
                  "data",
                  "titleIssue",
                  "data",
                  "info",
                  "dmvInfo",
                  "status",
                ],
                valuesForSelect: ["untitled", "titled", "not_checked"].map(
                  (x) => ({
                    label: capitalize(x) ?? "",
                    value: x,
                  })
                ),
              },
            ],
          },
          {
            id: "lastCheckedDate",
            label: "Last Checked",
            value: (deal) =>
              deal?.data?.titleIssue?.data?.info.lastCheckedDate
                ? formatDate(
                    deal?.data?.titleIssue?.data?.info.lastCheckedDate,
                    "short"
                  )
                : "N/A",
            filters: [
              {
                placeholder: "Last Checked",
                preview: (props: FilterPreviewProps) => (
                  <DateFilter
                    {...(props as unknown as DateFilterPreviewProps)}
                  />
                ),
                queryPath: [
                  "data",
                  "titleIssue",
                  "data",
                  "info",
                  "lastCheckedDate",
                ],
              },
            ],
            sortPath: ["data", "titleIssue", "data", "info", "lastCheckedDate"],
          },
          {
            id: "tickets",
            label: "Ticket",
            value: (deal) =>
              deal?.data?.lenderTickets?.length ? (
                <Button
                  variant="blue"
                  style={{
                    padding: "6px 10px",
                    fontSize: "14px",
                    height: "auto",
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate(`/tickets/${deal?.data?.lenderTickets?.[0]?._id}`);
                  }}
                >
                  Ticket
                </Button>
              ) : (
                "N/A"
              ),
          },
        ] as Column<Deal>[])
      : []),

    {
      id: "collateral",
      label: "Collateral",
      truncate: 15,
      value: (deal) => formatCollateral(deal),
      sortPath: ["data", "info", "vehicle", "make"],
      filters: [
        {
          placeholder: "Collateral",
          preview: TextFilter,
          queryPath: ["data", "info", "vehicle", "make"],
          partialSearch: true,
          caseInsensitive: true,
        },
      ],
    },
    {
      id: "vin",
      label: "Vin",
      truncate: 10,
      value: (deal) => deal?.data?.info?.vehicle?.VIN ?? "",
      sortPath: ["data", "info", "vehicle", "VIN"],
      filters: [
        {
          placeholder: "Vin",
          preview: TextFilter,
          queryPath: ["data", "info", "vehicle", "VIN"],
          partialSearch: true,
          caseInsensitive: true,
        },
      ],
    },
    ...(dealerId
      ? []
      : [
          {
            id: "dealer",
            label: "Dealership",
            truncate: 15,
            value: (deal: Deal) => deal?.data?.dealership?.data?.info?.name,
          },
        ]),
    {
      label: "Customer",
      value: (deal) => formatCustomersNames(deal),
      id: "customer",
      truncate: 15,
      filters: [
        {
          placeholder: "Customer",
          preview: ApplicantFilter,
          queryPath: [],
        },
      ],
    },
    {
      label: "State",
      value: (deal) =>
        States[deal?.data?.applicant?.data?.info?.currentState].prefix ?? "",
      id: "state",
      truncate: 15,
      filters: [
        {
          placeholder: "State",
          preview: TextFilter,
          queryPath: ["data", "applicant", "data", "info", "currentState"],
        },
      ],
    },
    {
      id: "contractDate",
      label: "Contract Date",
      value: (deal) =>
        formatDate(deal.data.info.dealDates?.contractDate, "short"),
      filters: [
        {
          placeholder: "Contract date",
          preview: (props: FilterPreviewProps) => (
            <DateFilter {...(props as unknown as DateFilterPreviewProps)} />
          ),
          queryPath: ["data", "info", "dealDates", "contractDate"],
        },
      ],
      sortPath: ["data", "info", "dealDates", "contractDate"],
    },

    {
      id: "actions",
      label: "Actions",
      value: (deal) => (
        <IconButton
          id={`open-deal-${deal?._id}`}
          className={"blueCircle"}
          onClick={() => {
            navigate(`/loans/${deal._id}?loanPage=${currentPage}`);
          }}
        >
          <AiOutlineArrowRight size={18} />
        </IconButton>
      ),
    },
  ];
  return (
    <div
      className={dealerId ? styles.tableContainerDealer : styles.tableContainer}
    >
      <h2 className={styles.title}>Loans</h2>
      <Table<Deal>
        query={{ ...(dealerId ? { "data.dealershipId": dealerId } : {}) }}
        columns={columns}
        useGetFunction={useGetDeals}
        queryParam="loanPage"
        entityType="loans"
      />
    </div>
  );
};

export default Applications;
